import React from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { Card } from '../../ui/Card';
import { RADIUS } from '../../../config/designTokens';
import { getAchievementIllustration } from '../../../mascot';
import { RARITY_CONFIG } from '../../../mascot/achievements/achievementData';
import { GoldenShimmer, LegendaryGlow } from './effects';
import { useAchievementShimmer } from './useAchievementShimmer';
import { AchievementProgressBar } from './AchievementProgressBar';
import type { AchievementCardProps } from './types';
export function CollectibleAchievementCard({
  achievement,
  isUnlocked,
  index,
  compact = false
}: AchievementCardProps & {
  compact?: boolean;
}) {
  const isNewlySeen = useAchievementShimmer(achievement.id, isUnlocked);
  const config = RARITY_CONFIG[achievement.rarity];
  const isHighlightRarity = achievement.rarity === 'rare' || achievement.rarity === 'legendary';
  const Illustration = getAchievementIllustration(achievement.id);
  if (!Illustration) return null;
  if (compact) {
    return <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: index * 0.1
    }} className={`relative flex items-center gap-3 p-2 ${RADIUS.card} border-2 transition-all overflow-hidden ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-80'} ${isNewlySeen ? 'animate-shine' : ''}`} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1836" data-mp="1ldz2v02zcluh">
        
        {isUnlocked && achievement.rarity === 'legendary' && <LegendaryGlow data-id="element-1837" data-mp="1biyss5h6svp1" />}
        {isUnlocked && achievement.rarity === 'rare' && <GoldenShimmer data-id="element-1838" data-mp="16j2m1yh3uq5s" />}

        <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden shadow-inner bg-white/50 relative z-10" data-id="element-1839" data-mp="16hrk8477zshb">
          <Illustration achievementId={achievement.id} isUnlocked={isUnlocked} rarity={achievement.rarity} variant={achievement.djayVariantUnlocked} size="sm" className="w-full h-full" data-id="element-1840" data-mp="2mvhpvfdzmot2" />
          
        </div>

        <div className="flex-1 min-w-0 relative z-10 py-1" data-id="element-1841" data-mp="1fkaak93y06l3">
          <div className="flex items-center justify-between gap-2 mb-1" data-id="element-1842" data-mp="1kiv9m57gkk7y">
            <h4 className={`font-bold text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1843" data-mp="1olhu1l1sv2v4">
              
              {achievement.name}
            </h4>
            {isUnlocked && isHighlightRarity && <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold ${config.badgeBg} ${config.badgeText}`} data-id="element-1844" data-mp="wult9it0koo7">
              
                {config.label}
              </span>}
          </div>

          {!isUnlocked && achievement.progress ? <AchievementProgressBar current={achievement.progress.current} max={achievement.progress.max} label={achievement.description} data-id="element-1845" data-mp="15tfiup7xges9" /> : <p className={`text-xs truncate ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1846" data-mp="d6324vog716v">
            
              {achievement.description}
            </p>}
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.02
  }} className="relative h-full" data-id="element-1847" data-mp="alepgrvzj41i">
      
      <Card className={`
          relative overflow-hidden transition-all duration-300 border-2 h-full flex flex-col p-0
          ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-70'}
          ${isUnlocked ? 'hover:shadow-md hover:-translate-y-0.5' : ''}
          ${isNewlySeen ? 'animate-shine' : ''}
        `} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1848" data-mp="5kr3ikmm5de5">
        
        {/* Illustration Header (60% of card) */}
        <div className="relative w-full aspect-video bg-white/40 border-b-2 border-white/20" data-id="element-1849" data-mp="393amewjej6wa">
          <Illustration achievementId={achievement.id} isUnlocked={isUnlocked} rarity={achievement.rarity} variant={achievement.djayVariantUnlocked} size="lg" className="w-full h-full" data-id="element-1850" data-mp="351pb3fmin0o7" />
          

          {!isUnlocked && <div className="absolute top-2 right-2 z-30" data-id="element-1851" data-mp="13zppk1vzn9er">
              <div className="w-6 h-6 rounded-full bg-slate-800/50 flex items-center justify-center backdrop-blur-sm" data-id="element-1852" data-mp="14098nlxdk3ud">
                <Lock className="w-3.5 h-3.5 text-white" data-id="element-1853" data-mp="182vzbjib7ipz" />
              </div>
            </div>}

          {isUnlocked && isHighlightRarity && <div className="absolute top-2 right-2 z-30" data-id="element-1854" data-mp="z7y05uokj9q7">
              <div className={`px-2 py-1 rounded-full text-[10px] font-bold shadow-sm ${config.badgeBg} ${config.badgeText}`} data-id="element-1855" data-mp="z6ej41v38und">
              
                {config.label}
              </div>
            </div>}
        </div>

        {/* Content Footer (40% of card) */}
        <div className="p-3 flex-1 flex flex-col relative z-10" data-id="element-1856" data-mp="bcd5bydavdom">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1857" data-mp="g9dc6bhz4kgs">
            <h4 className={`font-black text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1858" data-mp="gbzlogofda1n">
              
              {achievement.name}
            </h4>
          </div>
          <p className={`text-xs leading-snug mb-2 flex-1 ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1859" data-mp="2altr00ou56x9">
            
            {achievement.description}
          </p>

          {!isUnlocked && achievement.progress && <div className="mt-auto pt-2 border-t border-slate-200/50" data-id="element-1860" data-mp="2tk4zj2m92lzt">
              <AchievementProgressBar current={achievement.progress.current} max={achievement.progress.max} layout="labelled" data-id="element-1861" data-mp="2tlg4h5rflzsi" />
            
            </div>}
        </div>
      </Card>
    </motion.div>;
}